import React, { useEffect } from "react"
import { Box, Card, CardContent, Chip, Container, Divider, Grid, Stack, Typography } from "@mui/material"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import ImageSearchIcon from "@mui/icons-material/ImageSearch"
import SpeedIcon from "@mui/icons-material/Speed"

const FEATURES = [
  {
    icon: <AutoAwesomeIcon color="primary" />,
    title: "AI Listing Generator",
    description:
      "Enter a product name and category — or upload a photo — and receive a complete, marketplace-ready listing in under 5 seconds."
  },
  // {
  //   icon: <StorefrontIcon color="primary" />,
  //   title: "Amazon & Meesho Ready",
  //   description:
  //     "Listings are tuned to each platform's search algorithm — keyword-rich titles for Amazon, concise bullets for Meesho."
  // },
  {
    icon: <ImageSearchIcon color="primary" />,
    title: "AI Product Images",
    description:
      "Remove backgrounds and generate polished hero shots that meet marketplace image requirements with one click."
  },
  {
    icon: <SpeedIcon color="primary" />,
    title: "Blazing Fast",
    description: "Complete listings generated in under 5 seconds — no waiting, no writer's block."
  }
]

const AboutPage: React.FC = () => {
  useEffect(() => {
    document.title = "About | ListYourProduct"
  }, [])

  return (
    <Container maxWidth="md" sx={{ pb: 10 }}>
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={700}>
              About ListYourProduct
            </Typography>
            <Chip label="AI-Powered" size="small" color="primary" variant="outlined" />
          </Stack>
          <Typography variant="body1" color="text.secondary">
            ListYourProduct is a full-stack AI-powered tool that helps online sellers generate professional product
            listings and studio-quality images — ready to publish on Amazon and Meesho in seconds.
          </Typography>
        </Stack>

        <Divider />

        <Stack spacing={1.5}>
          <Typography variant="h6" fontWeight={700}>
            What we do
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We handle the copy, keywords, and images so you can focus on growing your catalogue — not writing
            descriptions. Upload a product photo, provide a name and category, and our AI generates SEO-optimised
            titles, compelling descriptions, and relevant keywords tuned for marketplace search algorithms.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every new account starts with free AI image credits — no credit card required. You can explore all core
            features before deciding to purchase more.
          </Typography>
        </Stack>

        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>
            Core capabilities
          </Typography>
          <Grid container spacing={2}>
            {FEATURES.map(feature => (
              <Grid item xs={12} sm={6} key={feature.title}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Box>{feature.icon}</Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>

        <Divider />

        <Stack spacing={1.5}>
          <Typography variant="h6" fontWeight={700}>
            Built for Indian marketplace sellers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fast, accurate, and built specifically for Indian marketplace sellers — ListYourProduct removes every
            friction point between your product and a live listing. Whether you manage 10 or 10,000 SKUs, our batch
            upload and generation tools let you refresh an entire catalogue in an afternoon.
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Questions? Contact us at{" "}
            <a href="mailto:reymishra08@gmail.com" style={{ color: "inherit" }}>
              reymishra08@gmail.com
            </a>
          </Typography>
        </Stack>
      </Stack>
    </Container>
  )
}

export default AboutPage
